import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, Check, Loader2, History, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { API } from '../utils/kitchenTasks';
import { formatApiError } from '../services/api';
import { formatTime } from '../utils/kitchenTasks';
import MemberAccountPanel from '../components/team/MemberAccountPanel';

const ROLE_LABEL = { super_admin: 'Super-admin', admin: 'Direction (admin)', chef: 'Chef', collaborator: 'Collaborateur' };

export default function AdminRolesPage() {
  const { hasPermission, user } = useAuth();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [emailStatus, setEmailStatus] = useState(null);
  const [openUser, setOpenUser] = useState(null);
  const [busyCell, setBusyCell] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, u, a, e] = await Promise.all([axios.get(`${API}/admin/roles`), axios.get(`${API}/admin/users`), axios.get(`${API}/admin/audit`), axios.get(`${API}/admin/email-status`)]);
      setData(r.data.data); setUsers(u.data.data); setAudit(a.data.data); setEmailStatus(e.data.data);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);
  useEffect(() => { if (hasPermission('manage_roles')) load(); }, [load, hasPermission]);

  if (user && !hasPermission('manage_roles')) return <Navigate to="/gerer" replace />;
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 text-neon-pink animate-spin" /></div>;
  const roles = data.roles.map(r => r.id);
  const canEditMatrix = user?.role === 'super_admin';
  const editable = (r) => canEditMatrix && (data.editable_roles || []).includes(r);

  const togglePerm = async (perm, role, enabled) => {
    const key = `${role}:${perm}`;
    if (busyCell) return;
    setBusyCell(key);
    try {
      const res = await axios.put(`${API}/admin/roles/${role}/permissions`, { permission: perm, enabled });
      setData(d => ({ ...d, matrix: res.data.data.matrix, roles: d.roles.map(r => r.id === role ? { ...r, permissions: res.data.data.permissions, customized: true } : r) }));
      toast.success(`${ROLE_LABEL[role]} : ${data.permission_labels?.[perm] || perm} ${enabled ? 'accordée' : 'retirée'}`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusyCell(null); }
  };

  const resetRole = async (role) => {
    if (!window.confirm(`Rétablir les permissions par défaut du rôle ${ROLE_LABEL[role]} ?`)) return;
    try { await axios.post(`${API}/admin/roles/${role}/reset`); toast.success('Permissions par défaut rétablies'); load(); } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="min-h-screen bg-white p-4 md:p-8 pb-32" data-testid="admin-roles-page">
      <div className="max-w-6xl mx-auto space-y-8">
        <div><h1 className="font-heading text-3xl font-bold text-gray-900 flex items-center gap-2"><ShieldCheck className="text-neon-pink" />Administration → Rôles et permissions</h1><p className="text-gray-500 text-sm">Réservé au super-administrateur. Les permissions sont vérifiées côté serveur sur chaque appel.</p></div>

        {emailStatus && (
          <div className={`rounded-2xl border px-4 py-3 text-sm ${emailStatus.configured ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-300 text-amber-800'}`} data-testid="email-status-banner">
            <strong>Emails ({emailStatus.provider})</strong> : {emailStatus.configured ? `actifs — expéditeur ${emailStatus.sender}` : `non configurés — les liens (mot de passe oublié, invitations) sont journalisés côté serveur et à transmettre manuellement. Renseignez RESEND_API_KEY et SENDER_EMAIL dans la configuration du serveur pour activer l'envoi.`}
          </div>
        )}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="roles-list">
          {data.roles.map(r => (
            <div key={r.id} className="border border-gray-200 rounded-2xl p-4">
              <div className="flex items-center justify-between"><h2 className="font-semibold text-gray-900">{ROLE_LABEL[r.id]}</h2><span className="text-xs text-gray-500">{r.permissions.length} permissions · {r.users.length} utilisateur(s)</span></div>
              <p className="text-sm text-gray-600 mt-1">{r.description}</p>
              <div className="flex flex-wrap gap-1 mt-2">{r.users.map(u => <button key={u.id} type="button" onClick={() => setOpenUser(users.find(x => x.id === u.id))} className={`text-xs px-2 py-1 rounded-full border min-h-[28px] ${u.active ? 'border-gray-200 text-gray-700 hover:border-neon-pink' : 'border-gray-200 text-gray-400 line-through'}`} data-testid={`role-user-${u.id}`}>{u.name || u.email}{u.custom_permissions.length ? ' ★' : ''}</button>)}</div>
            </div>
          ))}
        </section>

        <section data-testid="permissions-matrix">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
            <h2 className="font-heading text-xl font-bold text-gray-900">Matrice des permissions</h2>
            <p className="text-xs text-gray-500">{canEditMatrix ? 'Cochez / décochez une case pour modifier un rôle : effet immédiat pour tous ses utilisateurs (vérifié côté serveur). Le super-admin n\'est pas modifiable.' : 'Lecture seule (modification réservée au super-administrateur).'}</p>
          </div>
          <div className="overflow-x-auto border border-gray-200 rounded-2xl">
            <table className="w-full text-sm">
              <thead className="bg-gray-50"><tr><th className="text-left p-2 text-gray-600">Permission</th>{roles.map(r => {
                const info = data.roles.find(x => x.id === r);
                return (
                  <th key={r} className="p-2 text-gray-600 text-xs">
                    <div>{ROLE_LABEL[r]}</div>
                    {editable(r) && info?.customized && <button type="button" onClick={() => resetRole(r)} className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-700 hover:underline" data-testid={`reset-role-${r}`}><RotateCcw size={11} />Rétablir défaut</button>}
                  </th>
                );
              })}</tr></thead>
              <tbody>{Object.entries(data.permission_groups).map(([g, perms]) => (
                <React.Fragment key={g}>
                  <tr className="bg-neon-pink/5"><td colSpan={roles.length + 1} className="p-2 text-xs font-bold uppercase tracking-wide text-gray-700">{g}</td></tr>
                  {perms.map(p => (
                    <tr key={p} className="border-t border-gray-100" data-testid={`perm-row-${p}`}>
                      <td className="p-2">
                        <div className="font-mono text-xs text-gray-800">{p}</div>
                        {data.permission_labels?.[p] && <div className="text-[11px] text-gray-500">{data.permission_labels[p]}</div>}
                      </td>
                      {roles.map(r => {
                        const on = data.matrix[p][r];
                        const changed = data.defaults && data.defaults[p][r] !== on;
                        if (!editable(r)) return <td key={r} className="p-2 text-center">{on ? <Check size={16} className="inline text-emerald-600" /> : <span className="text-gray-300">—</span>}</td>;
                        const busy = busyCell === `${r}:${p}`;
                        return (
                          <td key={r} className="p-1 text-center">
                            <button type="button" onClick={() => togglePerm(p, r, !on)} disabled={!!busyCell} aria-pressed={on} title={changed ? 'Modifié par rapport au défaut' : ''}
                              className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${on ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100' : 'bg-white border-gray-200 text-gray-300 hover:border-gray-400'} ${changed ? 'ring-2 ring-amber-300' : ''}`}
                              data-testid={`perm-toggle-${r}-${p}`} data-enabled={on ? 'true' : 'false'}>
                              {busy ? <Loader2 size={14} className="animate-spin" /> : on ? <Check size={16} /> : <span>—</span>}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>))}</tbody>
            </table>
          </div>
        </section>

        <section data-testid="admin-users">
          <h2 className="font-heading text-xl font-bold text-gray-900 mb-2">Utilisateurs et permissions effectives</h2>
          <div className="space-y-2">{users.map(u => (
            <button key={u.id} type="button" onClick={() => setOpenUser(u)} className="w-full text-left border border-gray-200 rounded-2xl px-4 py-3 hover:bg-gray-50 min-h-[56px]" data-testid={`admin-user-${u.id}`}>
              <div className="flex flex-wrap items-center gap-2"><span className="font-semibold text-gray-900">{u.name}</span><span className="text-xs text-gray-500">{u.email}</span><span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200">{ROLE_LABEL[u.role]}</span>{!u.active && <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">Désactivé</span>}{u.team_member ? <span className="text-xs text-gray-500">Fiche : {u.team_member.name}</span> : <span className="text-xs text-amber-700">Sans fiche membre</span>}{u.custom_permissions?.length > 0 && <span className="text-xs text-violet-700">+{u.custom_permissions.length} permission(s) personnalisée(s)</span>}</div>
              <p className="text-xs text-gray-400 mt-1">{u.effective_permissions.length} permissions effectives</p>
            </button>))}</div>
        </section>

        <section data-testid="admin-audit">
          <h2 className="font-heading text-xl font-bold text-gray-900 mb-2 flex items-center gap-2"><History size={18} />Historique d'administration</h2>
          {audit.length === 0 ? <p className="text-sm text-gray-400">Aucune action enregistrée.</p> : <div className="space-y-1 text-sm">{audit.slice(0, 50).map((a, i) => <div key={i} className="border-b border-gray-100 py-1.5 text-gray-700"><span className="text-gray-400">{formatTime(a.at)}</span> · <strong>{a.actor_email}</strong> ({a.actor_role}) → {a.action} sur {a.target} : <span className="font-mono text-xs">{JSON.stringify(a.details)}</span></div>)}</div>}
        </section>
      </div>
      <MemberAccountPanel user={openUser} open={!!openUser} onOpenChange={(v) => { if (!v) setOpenUser(null); }} onChanged={load} allPermissions={data.all_permissions} />
    </div>
  );
}
